import { motion, type Variants } from "framer-motion";
import type { ReactNode } from "react";

import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";
import { usePreviewMode } from "@/lib/preview-mode";

type RevealProps = {
  children: ReactNode;
  className?: string;
  variants?: Variants;
  delay?: number;
  as?: "div" | "section" | "li" | "article" | "header" | "aside";
};

/** One-time, on-enter reveal. Reduced motion is handled globally by MotionConfig. */
export function Reveal({ children, className, variants = fadeUp, delay = 0, as = "div" }: RevealProps) {
  const Component = motion[as];
  // The admin preview shows final states: a scaled frame may never report the
  // element as having scrolled into view.
  const preview = usePreviewMode();
  return (
    <Component
      className={className}
      initial={preview ? "visible" : "hidden"}
      whileInView="visible"
      viewport={viewportOnce}
      variants={variants}
      transition={delay ? { delay } : undefined}
    >
      {children}
    </Component>
  );
}

/** Parent that staggers its Reveal children. */
export function RevealGroup({
  children,
  className,
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "ul" | "ol" | "section";
}) {
  const Component = motion[as];
  const preview = usePreviewMode();
  return (
    <Component
      className={className}
      initial={preview ? "visible" : "hidden"}
      whileInView="visible"
      viewport={viewportOnce}
      variants={staggerContainer}
    >
      {children}
    </Component>
  );
}
